import { Button } from '@hyperflake/react-ui-library';
import { CircularProgress } from '@library/loaders/components';
import React from 'react';

type Props = {
    title?: string;

    // Location
    isEditingLocation: boolean;
    tempLatitude: string;
    tempLongitude: string;
    onTempLatitudeChange: (value: string) => void;
    onTempLongitudeChange: (value: string) => void;

    onToggleEditOrApplyLocation: () => void;
    onCancelLocationEdit: () => void;

    // Unsaved
    hasUnsavedChanges: boolean;

    // View controls
    cameraAngle: number;
    cameraRange: number;
    cameraFOV: number;
    onCameraAngleChange: (value: number) => void;
    onCameraRangeChange: (value: number) => void;
    onCameraFOVChange: (value: number) => void;

    // Save
    onSave: () => void;
    isSubmitting?: boolean;

    // Optional: disable view sliders while editing location (your choice)
    disableViewControlsWhenEditingLocation?: boolean;
};

const MapCameraControlsPanel: React.FC<Props> = ({
    title = 'Camera View',

    isEditingLocation,
    tempLatitude,
    tempLongitude,
    onTempLatitudeChange,
    onTempLongitudeChange,
    onToggleEditOrApplyLocation,
    onCancelLocationEdit,

    hasUnsavedChanges,

    cameraAngle,
    cameraRange,
    cameraFOV,
    onCameraAngleChange,
    onCameraRangeChange,
    onCameraFOVChange,

    onSave,
    isSubmitting = false,

    disableViewControlsWhenEditingLocation = false,
}) => {
    const viewControlsDisabled = disableViewControlsWhenEditingLocation && isEditingLocation;

    return (
        <div className="absolute top-6 right-6 z-10 bg-white rounded-lg shadow-lg p-6 w-80 max-h-[calc(100%-3rem)] overflow-y-auto">
            <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
            </div>

            {/* Location Controls */}
            <div className="mb-4 pt-4 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 mb-2">Camera Location</label>

                {isEditingLocation && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-md">
                        <p className="text-xs text-blue-800 flex items-start gap-2">
                            <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                <path
                                    fillRule="evenodd"
                                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                                    clipRule="evenodd"
                                />
                            </svg>
                            <span>Click anywhere on the map to select a new location. Click “Save” to persist.</span>
                        </p>
                    </div>
                )}

                <div className="flex justify-between gap-1">
                    {/* Latitude */}
                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">Latitude</label>
                        <input
                            type="number"
                            step="any"
                            value={tempLatitude}
                            onChange={(e) => onTempLatitudeChange(e.target.value)}
                            disabled={!isEditingLocation}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="e.g., 25.276987"
                        />
                    </div>

                    {/* Longitude */}
                    <div className="mb-3">
                        <label className="block text-xs text-gray-600 mb-1">Longitude</label>
                        <input
                            type="number"
                            step="any"
                            value={tempLongitude}
                            onChange={(e) => onTempLongitudeChange(e.target.value)}
                            disabled={!isEditingLocation}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            placeholder="e.g., 55.296249"
                        />
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onToggleEditOrApplyLocation}
                        className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                            isEditingLocation
                                ? 'bg-orange-600 hover:bg-orange-700 text-white'
                                : 'bg-blue-600 hover:bg-blue-700 text-white'
                        }`}
                    >
                        {isEditingLocation ? 'Apply Location' : 'Change Location'}
                    </button>

                    {isEditingLocation && (
                        <button
                            type="button"
                            onClick={onCancelLocationEdit}
                            className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 rounded-md text-sm font-medium transition-colors"
                        >
                            Cancel
                        </button>
                    )}
                </div>
            </div>

            {/* Unsaved */}
            {hasUnsavedChanges && (
                <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                    <p className="text-xs text-yellow-800 flex items-center gap-2">
                        <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path
                                fillRule="evenodd"
                                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                                clipRule="evenodd"
                            />
                        </svg>
                        Unsaved changes
                    </p>
                </div>
            )}

            {/* Angle */}
            <div className={`mb-4 ${viewControlsDisabled ? 'opacity-60 pointer-events-none' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Direction Angle: {cameraAngle}°</label>
                <input
                    type="range"
                    min="0"
                    max="360"
                    value={cameraAngle}
                    onChange={(e) => onCameraAngleChange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>N (0°)</span>
                    <span>E (90°)</span>
                    <span>S (180°)</span>
                    <span>W (270°)</span>
                </div>
            </div>

            {/* Range */}
            <div className={`mb-4 ${viewControlsDisabled ? 'opacity-60 pointer-events-none' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Range: {cameraRange}m</label>
                <input
                    type="range"
                    min="10"
                    max="500"
                    step="10"
                    value={cameraRange}
                    onChange={(e) => onCameraRangeChange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>10m</span>
                    <span>500m</span>
                </div>
            </div>

            {/* FOV */}
            <div className={`mb-4 ${viewControlsDisabled ? 'opacity-60 pointer-events-none' : ''}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">Field of View: {cameraFOV}°</label>
                <input
                    type="range"
                    min="30"
                    max="180"
                    step="10"
                    value={cameraFOV}
                    onChange={(e) => onCameraFOVChange(Number(e.target.value))}
                    className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex justify-between text-xs text-gray-500 mt-1">
                    <span>30°</span>
                    <span>180°</span>
                </div>
            </div>

            <Button type="button" size="lg" className="w-full" onClick={onSave} disabled={isSubmitting}>
                {isSubmitting ? <CircularProgress size={16} color="inherit" /> : 'Save'}
            </Button>
        </div>
    );
};

export default MapCameraControlsPanel;
